import { memo } from 'react';
import PartsDiagram from 'components/parts-diagram';
import PartsOverviewMobileCard from 'components/parts-overview/parts-overview-mobile-card/parts-overview-mobile-card';
import { Conditional } from '@cat-ecom/pcc-components';
import styles from '../product-diagram-drawer.module.scss';
import cx from 'classnames';
import { PartDetailsAndSchematicResponse, LineItem } from 'types/parts-diagram';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { usePartsNavigation } from 'hooks/usePartsNavigation';
import PartsNavigationArrows from 'components/parts-navigation-arrows/PartsNavigationArrows';
import { CartResponse } from 'services/cart/types';
import { productPartsData } from 'types/product-diagram';

interface PartsCardsBaseProps {
  onSelectItem: (referenceNumber: string | null) => void;
  handleOpen: (
    partNumber: string,
    price: ProductDealerPriceAvailability
  ) => Promise<void>;
  openQuickViewDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  fromProductPage: boolean;
  contactDealerBtn?: React.ReactNode;
  allCardsHasContactDealer?: boolean;
  isImageShown: boolean;
  partsDiagramMobileView: boolean;
  currencyNotation: string;
  isRTL?: boolean;
  currencyLdFeatureFlag: boolean;
  isPccPdpHeaderUpdateMobile?: boolean;
}

interface ProductDiagramContentProps {
  isActiveListTab: boolean;
  selectedItem: string | null;
  partItem: PartDetailsAndSchematicResponse;
  handleOnHover: (refNumber: string) => void;
  lineItemsUpdated: LineItem[];
  isCardOverlay: boolean;
  partsCardsProps: PartsCardsBaseProps;
  isCatCorp: boolean;
  selectedEquipment: Equipment;
  shouldShowPartsDiagramV2: boolean;
  useNewZoomIcons?: boolean;
  contactDealerBtn?: React.ReactNode;
  recenterSvgOnPartTapMobile?: boolean;
  isMultiplePartDiagrams?: boolean;
  navigationProps?: {
    currentIndex: number;
    totalImages: number;
    shouldShowNavigation: boolean;
    handlePrevious: () => void;
    handleNext: () => void;
  };
  cartDetails?: CartResponse;
  productData?: productPartsData;
}

export const ProductDiagramContent = ({
  isActiveListTab,
  selectedItem,
  partItem,
  handleOnHover,
  lineItemsUpdated,
  isCardOverlay,
  partsCardsProps,
  isCatCorp,
  selectedEquipment,
  shouldShowPartsDiagramV2,
  useNewZoomIcons,
  contactDealerBtn,
  recenterSvgOnPartTapMobile,
  isMultiplePartDiagrams = false,
  navigationProps,
  cartDetails,
  productData
}: ProductDiagramContentProps) => {
  const renderPartsCards = () => {
    let itemsToRender = [];
    if (isActiveListTab) {
      itemsToRender = lineItemsUpdated;
    } else if (selectedItem) {
      itemsToRender = lineItemsUpdated?.filter(
        (item) => item.referenceNumber === selectedItem
      );
    }
    const showCloseIcon = isCardOverlay && !isActiveListTab;

    const hasInvalidReferenceCards =
      isActiveListTab &&
      lineItemsUpdated?.some((item) => !item?.referenceNumber);

    return (
      <Conditional test={!!itemsToRender?.length} fallback={null}>
        <div
          className={cx({
            [styles['card-overlay-wrapper']]: isCardOverlay && !isActiveListTab,
            'mt-4': !isCardOverlay && !isActiveListTab,
            'pb-5': isActiveListTab && !hasInvalidReferenceCards
          })}
        >
          {itemsToRender?.map((item, index) => {           
            const uniqueKey = `${item?.partNumber}-${item?.name}`;
            if (!item?.referenceNumber) {
              return null;
            }

            return (
              <div
                className={cx({
                  'mb-4':
                    isActiveListTab || (!isActiveListTab && !isCardOverlay)
                })}
                key={uniqueKey}
              >
                <PartsOverviewMobileCard
                  key={uniqueKey}
                  {...partsCardsProps}
                  partItem={item}
                  selectedItem={selectedItem}
                  showCloseIcon={showCloseIcon}
                  isCatCorp={isCatCorp}
                  selectedEquipment={selectedEquipment}
                  isActiveListTab={isActiveListTab}
                  isCardOverlay={isCardOverlay}
                  shouldShowPartsDiagramV2={shouldShowPartsDiagramV2}
                  isPccPdpHeaderUpdateMobile={true}
                  contactDealerBtn={contactDealerBtn}
                  cartDetails={cartDetails}
                  productData={productData}
                />
              </div>
            );
          })}
        </div>
      </Conditional>
    );
  };

  const renderInvalidReferenceCards = () => {
    let itemsToRender = [];
    if (isActiveListTab) {
      itemsToRender = lineItemsUpdated?.filter(
        (item) => !item?.referenceNumber
      );
    }

    return (
      <Conditional test={!!itemsToRender?.length} fallback={null}>
        <div className="pb-5">
          {itemsToRender?.map((item, index) => {
            const uniqueKey = `invalid-ref-${item?.partNumber}-${item?.name}`;

            return (
              <div
                className={cx({
                  'mb-4': isActiveListTab
                })}
                key={uniqueKey}
              >
                <PartsOverviewMobileCard
                  key={uniqueKey}
                  {...partsCardsProps}
                  partItem={item}
                  selectedItem={selectedItem}
                  showCloseIcon={false}
                  isCatCorp={isCatCorp}
                  selectedEquipment={selectedEquipment}
                  isActiveListTab={isActiveListTab}
                  isCardOverlay={false}
                  shouldShowPartsDiagramV2={shouldShowPartsDiagramV2}
                  isPccPdpHeaderUpdateMobile={true}
                  contactDealerBtn={contactDealerBtn}
                />
              </div>
            );
          })}
        </div>
      </Conditional>
    );
  };

  // Always call the hook, but use navigationProps if provided
  const localNavigationData = usePartsNavigation({
    partsDetails: partItem,
    isMultiplePartDiagrams
  });

  const navigationData = navigationProps || localNavigationData;

  const {
    currentIndex,
    totalImages,
    shouldShowNavigation,
    handlePrevious,
    handleNext
  } = navigationData;

  return (
    <>
      {!isActiveListTab && (
        <>
          <PartsNavigationArrows
            onPrevious={handlePrevious}
            onNext={handleNext}
            currentIndex={currentIndex}
            totalImages={totalImages}
            shouldShow={shouldShowNavigation}
            isMobile={true}
          />
          <PartsDiagram
            key={`diagram-${currentIndex}`}
            hoveredItem={selectedItem}
            svgContent={partItem?.graphicSVG || ''}
            isLoading={!partItem?.graphicSVG && !partItem?.lineItems?.length}
            partDetailsAndSchematicsData={partItem}
            isZoomEnabled={true}
            handleOnHover={handleOnHover}
            isMobile={true}
            isPccPdpPartDiagramUpdateFlag={true}
            useNewZoomIcons={useNewZoomIcons}
            recenterSvgOnPartTapMobile={recenterSvgOnPartTapMobile}
            isDiagramTabActive={!isActiveListTab}
          />
        </>
      )}
      {renderPartsCards()}
      {isActiveListTab && renderInvalidReferenceCards()}
    </>
  );
};

export default memo(ProductDiagramContent);
